import React, { useState, useRef, useEffect } from 'react';
import { 
  FaRobot, 
  FaTimes, 
  FaPaperPlane, 
  FaUser, 
  FaExpand, 
  FaCompress, 
  FaLightbulb, 
  FaClock, 
  FaQuestionCircle, 
  FaChevronRight, 
  FaStar 
} from 'react-icons/fa';
import { hrKnowledge, quickQuestions, suggestedTopics } from './chatbotData';
import './Chatbot.css';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! I'm Humanex HR Assistant 🤖 How can I help you with HR policies, benefits, or employee queries today?",
      sender: 'bot',
      timestamp: new Date(),
      type: 'greeting',
      quickReplies: ['Leave Policy', 'Benefits Info', 'Onboarding Process', 'Payroll Questions']
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    try {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } catch (err) {
      console.warn('Scroll error:', err);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Focus input when chatbot opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  // Save conversation history
  useEffect(() => {
    if (messages.length > 1) {
      setConversationHistory(messages.slice(-5));
    }
  }, [messages]);

  // Simulate bot typing with error handling
  const simulateTyping = (response, messageType = 'text', quickReplies = []) => {
    setIsTyping(true);
    setError(null);
    
    setTimeout(() => {
      try {
        const newMessage = {
          id: Date.now(),
          text: response,
          sender: 'bot',
          timestamp: new Date(),
          type: messageType,
          quickReplies: quickReplies
        };
        setMessages(prev => [...prev, newMessage]);
      } catch (err) {
        setError('Failed to send message. Please try again.');
        console.error('Message error:', err);
      } finally {
        setIsTyping(false);
      }
    }, 800 + Math.random() * 500);
  };

  // Enhanced response matching with error handling
  const getBotResponse = (userMessage) => {
    try {
      const message = userMessage.toLowerCase().trim();
      let quickReplies = [];

      // Response logic with improved matching
      if (message.includes('hello') || message.includes('hi') || message.includes('hey')) {
        const greeting = hrKnowledge.greetings[Math.floor(Math.random() * hrKnowledge.greetings.length)];
        quickReplies = ['HR Policies', 'Leave Balance', 'Benefits', 'Contact HR'];
        return { response: greeting, type: 'greeting', quickReplies };
      }
      else if (message.includes('onboard') || message.includes('new hire') || message.includes('joining')) {
        if (message.includes('doc') || message.includes('paper') || message.includes('document')) {
          const response = hrKnowledge.onboarding.documents[Math.floor(Math.random() * hrKnowledge.onboarding.documents.length)];
          quickReplies = ['Onboarding Timeline', 'Equipment Setup', 'Training Schedule', 'Back'];
          return { response: response, type: 'onboarding', quickReplies };
        } else if (message.includes('time') || message.includes('long') || message.includes('duration')) {
          const response = hrKnowledge.onboarding.timeline[Math.floor(Math.random() * hrKnowledge.onboarding.timeline.length)];
          quickReplies = ['Required Documents', 'First Day Guide', 'Mentor Program', 'Back'];
          return { response: response, type: 'onboarding', quickReplies };
        } else {
          const response = hrKnowledge.onboarding.process[Math.floor(Math.random() * hrKnowledge.onboarding.process.length)];
          quickReplies = ['Document Checklist', 'Timeline Details', 'FAQ', 'Contact Onboarding Team'];
          return { response: response, type: 'onboarding', quickReplies };
        }
      }
      else if (message.includes('leave') || message.includes('vacation') || message.includes('time off')) {
        if (message.includes('type') || message.includes('kind') || message.includes('category')) {
          const response = hrKnowledge.leave.types[Math.floor(Math.random() * hrKnowledge.leave.types.length)];
          quickReplies = ['Apply for Leave', 'Check Balance', 'Leave Policy', 'Emergency Leave'];
          return { response: response, type: 'leave', quickReplies };
        } else if (message.includes('request') || message.includes('apply') || message.includes('submit')) {
          const response = hrKnowledge.leave.process[Math.floor(Math.random() * hrKnowledge.leave.process.length)];
          quickReplies = ['Leave Application Form', 'Check Approvals', 'Cancel Leave', 'Back'];
          return { response: response, type: 'leave', quickReplies };
        } else if (message.includes('balance') || message.includes('remaining') || message.includes('available')) {
          const response = hrKnowledge.leave.balance[Math.floor(Math.random() * hrKnowledge.leave.balance.length)];
          quickReplies = ['Apply Leave', 'Leave History', 'Carry Forward', 'Back'];
          return { response: response, type: 'leave', quickReplies };
        } else {
          const response = hrKnowledge.leave.types[0] + "\n\n" + hrKnowledge.leave.process[0];
          quickReplies = ['Leave Types', 'Application Process', 'Balance Inquiry', 'Contact HR'];
          return { response: response, type: 'leave', quickReplies };
        }
      }
      else if (message.includes('benefit') || message.includes('insurance') || message.includes('health')) {
        if (message.includes('health') || message.includes('medical')) {
          const response = hrKnowledge.benefits.healthcare[Math.floor(Math.random() * hrKnowledge.benefits.healthcare.length)];
          quickReplies = ['Dental & Vision', 'Wellness Programs', 'Dependent Coverage', 'Back'];
          return { response: response, type: 'benefits', quickReplies };
        } else if (message.includes('retirement') || message.includes('401') || message.includes('pension')) {
          const response = hrKnowledge.benefits.retirement[Math.floor(Math.random() * hrKnowledge.benefits.retirement.length)];
          quickReplies = ['Contribution Limits', 'Investment Options', 'Withdrawal Rules', 'Back'];
          return { response: response, type: 'benefits', quickReplies };
        } else if (message.includes('flex') || message.includes('spending')) {
          const response = hrKnowledge.benefits.other[Math.floor(Math.random() * hrKnowledge.benefits.other.length)];
          quickReplies = ['Healthcare Plans', 'Retirement Plans', 'Other Benefits', 'Back'];
          return { response: response, type: 'benefits', quickReplies };
        } else {
          const response = hrKnowledge.benefits.healthcare[0] + "\n\n" + hrKnowledge.benefits.retirement[0] + "\n\n" + hrKnowledge.benefits.other[0];
          quickReplies = ['Medical Insurance', 'Retirement Plans', 'Flex Benefits', 'Contact Benefits Team'];
          return { response: response, type: 'benefits', quickReplies };
        }
      }
      else if (message.includes('payroll') || message.includes('salary') || message.includes('pay')) {
        if (message.includes('schedule') || message.includes('when') || message.includes('date')) {
          const response = hrKnowledge.payroll.schedule[Math.floor(Math.random() * hrKnowledge.payroll.schedule.length)];
          quickReplies = ['Pay Stub Access', 'Tax Forms', 'Direct Deposit', 'Back'];
          return { response: response, type: 'payroll', quickReplies };
        } else if (message.includes('doc') || message.includes('stub') || message.includes('w2')) {
          const response = hrKnowledge.payroll.documents[Math.floor(Math.random() * hrKnowledge.payroll.documents.length)];
          quickReplies = ['Pay Schedule', 'Tax Questions', 'Correction Request', 'Back'];
          return { response: response, type: 'payroll', quickReplies };
        } else if (message.includes('problem') || message.includes('issue') || message.includes('error')) {
          const response = hrKnowledge.payroll.issues[Math.floor(Math.random() * hrKnowledge.payroll.issues.length)];
          quickReplies = ['Contact Payroll', 'Submit Correction', 'View Pay History', 'Back'];
          return { response: response, type: 'payroll', quickReplies };
        } else {
          const response = hrKnowledge.payroll.schedule[0] + "\n\n" + hrKnowledge.payroll.documents[0];
          quickReplies = ['Pay Dates', 'Access Pay Stubs', 'Tax Information', 'Report Issue'];
          return { response: response, type: 'payroll', quickReplies };
        }
      }
      else if (message.includes('performance') || message.includes('review') || message.includes('feedback')) {
        if (message.includes('goal') || message.includes('target')) {
          const response = hrKnowledge.performance.goals[Math.floor(Math.random() * hrKnowledge.performance.goals.length)];
          quickReplies = ['Review Process', 'Feedback Submission', 'Career Growth', 'Back'];
          return { response: response, type: 'performance', quickReplies };
        } else if (message.includes('feedback') || message.includes('comment')) {
          const response = hrKnowledge.performance.feedback[Math.floor(Math.random() * hrKnowledge.performance.feedback.length)];
          quickReplies = ['Self-Assessment', 'Peer Feedback', 'Manager Review', 'Back'];
          return { response: response, type: 'performance', quickReplies };
        } else {
          const response = hrKnowledge.performance.reviews[Math.floor(Math.random() * hrKnowledge.performance.reviews.length)];
          quickReplies = ['Set Goals', 'Feedback Tools', 'Development Plan', 'Schedule Review'];
          return { response: response, type: 'performance', quickReplies };
        }
      }
      else if (message.includes('policy') || message.includes('rule') || message.includes('handbook')) {
        if (message.includes('remote') || message.includes('work from home')) {
          const response = hrKnowledge.policies.remote[Math.floor(Math.random() * hrKnowledge.policies.remote.length)];
          quickReplies = ['Hybrid Policy', 'Equipment Policy', 'Communication Guidelines', 'Back'];
          return { response: response, type: 'policies', quickReplies };
        } else if (message.includes('attendance') || message.includes('time') || message.includes('hour')) {
          const response = hrKnowledge.policies.attendance[Math.floor(Math.random() * hrKnowledge.policies.attendance.length)];
          quickReplies = ['Flexible Hours', 'Overtime Policy', 'Leave Policy', 'Back'];
          return { response: response, type: 'policies', quickReplies };
        } else if (message.includes('dress') || message.includes('code') || message.includes('attire')) {
          const response = hrKnowledge.policies.general[Math.floor(Math.random() * hrKnowledge.policies.general.length)];
          quickReplies = ['Remote Policy', 'Attendance Policy', 'Conduct Policy', 'Back'];
          return { response: response, type: 'policies', quickReplies };
        } else {
          const response = hrKnowledge.policies.general[Math.floor(Math.random() * hrKnowledge.policies.general.length)];
          quickReplies = ['Remote Work', 'Attendance', 'Code of Conduct', 'Full Handbook'];
          return { response: response, type: 'policies', quickReplies };
        }
      }
      else if (message.includes('thank') || message.includes('thanks') || message.includes('appreciate')) {
        const thanksResponses = [
          "You're welcome! 😊 Is there anything else I can help you with?",
          "Happy to help! Let me know if you need anything else.",
          "Glad I could assist! Feel free to ask more questions.",
          "You're very welcome! Don't hesitate to reach out again."
        ];
        quickReplies = ['More Questions', 'Give Feedback', 'Contact Human', 'End Chat'];
        return { response: thanksResponses[Math.floor(Math.random() * thanksResponses.length)], type: 'thanks', quickReplies };
      }
      else if (message.includes('bye') || message.includes('goodbye') || message.includes('see you')) {
        return { response: "Goodbye! 👋 Feel free to ask if you have more HR questions. Have a great day!", type: 'farewell', quickReplies: [] };
      }
      else if (message.includes('help') || message.includes('what can you do')) {
        const helpResponse = "I can help you with:\n\n• 📋 **HR Policies** - Remote work, attendance, code of conduct\n• 🏖️ **Leave & Time Off** - Types, application process, balances\n• 🏥 **Benefits** - Healthcare, retirement, wellness programs\n• 💰 **Payroll** - Schedule, documents, issues\n• 🎯 **Performance** - Reviews, goals, feedback\n• 🚀 **Onboarding** - Process, documents, timeline\n\nWhat would you like to know more about?";
        quickReplies = ['HR Policies', 'Leave Benefits', 'Payroll', 'Onboarding'];
        return { response: helpResponse, type: 'help', quickReplies };
      }
      else if (message.includes('contact') || message.includes('human') || message.includes('speak to')) {
        const contactResponse = "I can connect you with our HR team:\n\n📞 **HR Help Desk:** extension 5555\n📧 **Email:** hr@company.com\n🏢 **Office:** Building A, 3rd Floor\n⏰ **Hours:** Mon-Fri 9AM-5PM\n\nWould you like me to:\n1. Schedule a call back\n2. Send an email template\n3. Provide specific department contacts";
        quickReplies = ['Schedule Callback', 'Email Template', 'Department Contacts', 'Back'];
        return { response: contactResponse, type: 'contact', quickReplies };
      }
      else {
        const defaultResponse = hrKnowledge.default[Math.floor(Math.random() * hrKnowledge.default.length)];
        quickReplies = ['HR Policies', 'Leave Questions', 'Benefits Info', 'Contact HR'];
        return { response: defaultResponse, type: 'suggestions', quickReplies };
      }
    } catch (err) {
      console.error('Error generating response:', err);
      return { 
        response: "I apologize, but I encountered an error processing your request. Please try again or contact HR support.", 
        type: 'error',
        quickReplies: ['Try Again', 'Contact HR', 'Help Topics', 'Clear Chat']
      };
    }
  };

  // Handle sending messages with error handling
  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    try {
      // Add user message
      const userMessage = {
        id: Date.now(),
        text: inputMessage,
        sender: 'user',
        timestamp: new Date(),
        type: 'text'
      };

      setMessages(prev => [...prev, userMessage]);

      // Get and send bot response
      const botResponse = getBotResponse(inputMessage);
      simulateTyping(botResponse.response, botResponse.type, botResponse.quickReplies);

      setInputMessage('');
      setError(null);
    } catch (err) {
      setError('Failed to send message. Please try again.');
      console.error('Send message error:', err);
    }
  };

  // Handle quick question click with error handling
  const handleQuickQuestion = (question) => {
    setInputMessage(question);
    
    setTimeout(() => {
      try {
        const userMessage = {
          id: Date.now(),
          text: question,
          sender: 'user',
          timestamp: new Date(),
          type: 'text'
        };

        setMessages(prev => [...prev, userMessage]);

        const botResponse = getBotResponse(question);
        simulateTyping(botResponse.response, botResponse.type, botResponse.quickReplies);

        setInputMessage('');
      } catch (err) {
        setError('Failed to process quick question. Please try again.');
        console.error('Quick question error:', err);
      }
    }, 100);
  };

  // Handle quick reply click with error handling
  const handleQuickReply = (reply) => {
    try {
      const userMessage = {
        id: Date.now(),
        text: reply,
        sender: 'user',
        timestamp: new Date(),
        type: 'text'
      };

      setMessages(prev => [...prev, userMessage]);

      const botResponse = getBotResponse(reply);
      simulateTyping(botResponse.response, botResponse.type, botResponse.quickReplies);
    } catch (err) {
      setError('Failed to process quick reply. Please try again.');
      console.error('Quick reply error:', err);
    }
  };

  // Format timestamp
  const formatTime = (date) => {
    try {
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    } catch (err) {
      return 'Just now';
    }
  };

  // Format date for conversation starter
  const formatDate = (date) => {
    try {
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric'
      });
    } catch (err) {
      return 'Today';
    }
  };

  // Toggle chatbot window
  const toggleChatbot = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setIsMinimized(false);
    }
  };

  // Toggle minimize
  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  // Clear conversation
  const clearConversation = () => {
    setMessages([{
      id: 1,
      text: "Hello! I'm Humanex HR Assistant 🤖 How can I help you with HR policies, benefits, or employee queries today?",
      sender: 'bot',
      timestamp: new Date(),
      type: 'greeting',
      quickReplies: ['Leave Policy', 'Benefits Info', 'Onboarding Process', 'Payroll Questions']
    }]);
    setError(null);
  };

  return (
    <div className="chati-container">
      {/* Error Display */}
      {error && (
        <div className="chati-error-alert">
          <div className="chati-error-content">
            ⚠️ {error}
            <button 
              onClick={() => setError(null)}
              className="chati-error-close"
              aria-label="Close error message"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Floating Chat Button */}
      {!isOpen && (
        <button className="chati-floating-btn" onClick={toggleChatbot} aria-label="Open HR Assistant">
          <div className="chati-btn-icon">
            <FaRobot />
          </div>
          {/* <span className="chati-btn-text">HR Assistant</span> */}
          {/* <div className="chati-notification-badge">1</div> */}
          <div className="chati-pulse-effect"></div>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className={`chati-window ${isMinimized ? 'chati-minimized' : ''}`}>
          {/* Header */}
          <div className="chati-header">
            <div className="chati-header-left">
              <div className="chati-avatar">
                <FaRobot />
                <div className="chati-status-indicator"></div>
              </div>
              <div className="chati-header-info">
                <h3 className="chati-title">
                  <span className="chati-title-main">Humanex AI</span>
                  <span className="chati-title-sub">HR Assistant</span>
                </h3>
                <div className="chati-status">
                  <span className="chati-status-dot"></span>
                  <span className="chati-status-text">Online • Typically replies instantly</span>
                </div>
              </div>
            </div>
            <div className="chati-header-actions">
              <button 
                className="chati-action-btn chati-minimize-btn"
                onClick={toggleMinimize}
                title={isMinimized ? "Expand" : "Minimize"}
                aria-label={isMinimized ? "Expand chat" : "Minimize chat"}
              >
                {isMinimized ? <FaExpand /> : <FaCompress />}
              </button>
              <button 
                className="chati-action-btn chati-close-btn"
                onClick={toggleChatbot}
                title="Close"
                aria-label="Close chat"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          {/* Welcome Banner */}
          {!isMinimized && messages.length <= 2 && suggestedTopics && suggestedTopics.length > 0 && (
            <div className="chati-welcome-banner">
              <div className="chati-welcome-content">
                <FaLightbulb className="chati-welcome-icon" />
                <div className="chati-welcome-text">
                  <h4>Welcome to HR Assistant!</h4>
                  <p>Get instant answers to your HR questions</p>
                </div>
              </div>
              <div className="chati-welcome-date">{formatDate(new Date())}</div>
            </div>
          )}

          {/* Messages Container */}
          {!isMinimized && (
            <>
              <div className="chati-messages-container">
                <div className="chati-messages">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`chati-message ${
                        message.sender === 'user' ? 'chati-message-user' : 'chati-message-bot'
                      } ${
                        message.type === 'greeting' ? 'chati-message-greeting' : 
                        message.type === 'help' ? 'chati-message-help' : 
                        message.type === 'error' ? 'chati-message-error' : ''
                      }`}
                    >
                      <div className="chati-message-content">
                        {message.sender === 'bot' && (
                          <div className="chati-message-avatar">
                            <FaRobot />
                          </div>
                        )}
                        <div className="chati-message-bubble">
                          <div className="chati-message-text">
                            {message.text.split('\n').map((line, i) => (
                              <p key={i}>{line}</p>
                            ))}
                          </div>
                          <div className="chati-message-footer">
                            <span className="chati-message-time">
                              <FaClock /> {formatTime(message.timestamp)}
                            </span>
                            {message.sender === 'bot' && message.type && (
                              <span className={`chati-message-type chati-message-type-${message.type}`}>
                                {message.type.charAt(0).toUpperCase() + message.type.slice(1)}
                              </span>
                            )}
                          </div>
                        </div>
                        {message.sender === 'user' && (
                          <div className="chati-message-avatar chati-user-avatar">
                            <FaUser />
                          </div>
                        )}
                      </div>
                      
                      {/* Quick Replies */}
                      {message.sender === 'bot' && message.quickReplies && message.quickReplies.length > 0 && (
                        <div className="chati-quick-replies">
                          {message.quickReplies.map((reply, index) => (
                            <button
                              key={index}
                              className="chati-quick-reply-btn"
                              onClick={() => handleQuickReply(reply)}
                              disabled={isTyping}
                              aria-label={`Quick reply: ${reply}`}
                            >
                              <FaChevronRight className="chati-quick-reply-icon" />
                              {reply}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Typing Indicator */}
                  {isTyping && (
                    <div className="chati-typing-indicator">
                      <div className="chati-typing-avatar">
                        <FaRobot />
                      </div>
                      <div className="chati-typing-content">
                        <div className="chati-typing-dots">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                        <div className="chati-typing-text">Humanex is typing...</div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} className="chati-messages-end" />
                </div>

                {/* Suggested Topics */}
                {suggestedTopics && messages.length <= 3 && (
                  <div className="chati-suggested-topics">
                    <div className="chati-topics-header">
                      <FaStar className="chati-topics-icon" />
                      <h4>Popular Topics</h4>
                    </div>
                    <div className="chati-topics-grid">
                      {suggestedTopics.slice(0, 6).map((topic, index) => (
                        <div 
                          key={index} 
                          className="chati-topic-card"
                          onClick={() => handleQuickQuestion(topic.question)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === 'Enter' && handleQuickQuestion(topic.question)}
                          aria-label={`Ask about ${topic.title}: ${topic.description}`}
                        >
                          <div className="chati-topic-icon">
                            {topic.icon}
                          </div>
                          <div className="chati-topic-content">
                            <h5>{topic.title}</h5>
                            <p>{topic.description}</p>
                          </div>
                          <FaChevronRight className="chati-topic-arrow" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Conversation History */}
                {conversationHistory.length > 0 && messages.length > 5 && (
                  <div className="chati-history-prompt">
                    <button 
                      className="chati-history-btn"
                      onClick={clearConversation}
                      aria-label="Start new conversation"
                    >
                      Start New Conversation
                    </button>
                  </div>
                )}
              </div>

              {/* Input Area */}
              <div className="chati-input-container">
                <form onSubmit={handleSendMessage} className="chati-input-form">
                  <div className="chati-input-wrapper">
                    <input
                      ref={inputRef}
                      type="text"
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      placeholder="Type your HR question here..."
                      className="chati-input"
                      disabled={isTyping}
                      aria-label="Type your HR question"
                    />
                    <div className="chati-input-actions">
                      <button
                        type="button"
                        className="chati-action-btn chati-help-btn"
                        onClick={() => handleQuickQuestion("What can you help me with?")}
                        title="Get Help"
                        disabled={isTyping}
                        aria-label="Get help on what I can ask"
                      >
                        <FaQuestionCircle />
                      </button>
                      <button
                        type="submit"
                        className="chati-send-btn"
                        disabled={!inputMessage.trim() || isTyping}
                        aria-label="Send message"
                      >
                        <FaPaperPlane />
                        <span className="chati-send-text">Send</span>
                      </button>
                    </div>
                  </div>
                  {/* <div className="chati-quick-questions">
                    <span className="chati-quick-label">Quick asks:</span>
                    {quickQuestions && quickQuestions.slice(0, 4).map((question, index) => (
                      <button
                        key={index}
                        className="chati-quick-btn"
                        onClick={() => handleQuickQuestion(question)}
                        type="button"
                        disabled={isTyping}
                        aria-label={`Quick ask: ${question}`}
                      >
                        {question}
                      </button>
                    ))}
                  </div> */}
                </form>
                <div className="chati-footer">
                  <span className="chati-footer-text">
                    Powered by Humanex AI • Your data is secure
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Chatbot;